/**
 * Invoice activity timeline — prototype-shaped audit rail. Server-paginated:
 * the parent owns the `useInfiniteQuery` and hands the flattened entries +
 * fetch handles to this component. A "Load more" button appears at the
 * bottom while another page is available on the server.
 */
import { Bell, Check, FileMinus, FileText, HandCoins, Pencil, Send } from "lucide-react";
import type { reports as reportsContract } from "@flint/api-contract";
import type { ReactNode } from "react";
import type { z } from "zod";
import { formatDateTime, paiseToInr } from "../../../lib/format.ts";

type Feed = z.infer<typeof reportsContract.invoiceActivityFeedOutput>;
type Entry = Feed["entries"][number];

export interface InvoiceTimelineFetchState {
  /** True when the server has another older page beyond what's loaded. */
  hasMore: boolean;
  /** True while `fetchNextPage()` is in flight. */
  isFetching: boolean;
  onLoadMore: () => void;
}

export interface InvoiceTimelineProps {
  /** Invoice id, kept for future deep-link affordances on individual rows. */
  invoiceId: string;
  /** Flattened audit rows across all loaded pages, newest first. */
  entries: readonly Entry[];
  /** True only during the first page fetch — drives the "loading" sub-line. */
  isLoading: boolean;
  /** Server-pagination handles. */
  fetch: InvoiceTimelineFetchState;
}

/** Render the invoice activity feed in the prototype timeline frame. */
export function InvoiceTimeline({ entries, isLoading, fetch: fetchState }: InvoiceTimelineProps) {
  const showLoadMore = fetchState.hasMore;
  const sub = isLoading
    ? "Activity feed loading…"
    : showLoadMore
      ? `Audit trail · ${entries.length}+ events`
      : `Audit trail · ${entries.length} events since invoice issued`;

  return (
    <section className="timeline-card" aria-label="Activity timeline">
      <div className="head">
        <div>
          <h3>Activity</h3>
          <p className="sub">{sub}</p>
        </div>
      </div>
      {isLoading ? null : entries.length === 0 ? (
        <div className="timeline-empty">No activity recorded yet.</div>
      ) : (
        <>
          <ol className="timeline">
            {entries.map((entry) => (
              <TimelineItem key={entry.id} entry={entry} />
            ))}
          </ol>
          {showLoadMore ? (
            <div className="timeline-loadmore">
              <button
                type="button"
                onClick={fetchState.onLoadMore}
                disabled={fetchState.isFetching}
              >
                {fetchState.isFetching ? "Loading…" : "Load more"}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function TimelineItem({ entry }: { entry: Entry }) {
  const model = entryModel(entry);
  return (
    <li className={`timeline-item s-${model.tone}`}>
      <span className="timeline-marker" aria-hidden>
        {markerIcon(model.tone)}
      </span>
      <div className="timeline-content">
        <div className="head">
          <span className="what">{model.what}</span>
          <span className="when">{formatDateTime(entry.at)}</span>
          <span className="who">· by {entry.actorName ?? actorLabel(entry.actorType)}</span>
        </div>
        {model.body ? <div className="body">{model.body}</div> : null}
      </div>
    </li>
  );
}

function entryModel(entry: Entry): {
  tone: "paid" | "sent" | "issued" | "reminder" | "warn" | "draft";
  what: string;
  body: ReactNode;
} {
  const m = entry.metadata;
  if (entry.entityType === "receipt" && entry.verb === "recorded") {
    return {
      tone: "paid",
      what: "Receipt recorded",
      body: (
        <>
          Applied <span className="num">{formatPaise(m.amountPaise)}</span>
          {m.tdsPaise && m.tdsPaise !== "0" ? (
            <>
              {" "}
              incl. <span className="num">{formatPaise(m.tdsPaise)}</span> TDS
            </>
          ) : null}{" "}
          via {(m.mode as string) ?? "unknown mode"}.
        </>
      ),
    };
  }
  if (entry.entityType === "credit-note" && entry.verb === "raised") {
    return {
      tone: "warn",
      what: "Credit note raised",
      body: (
        <>
          <span className="num">{(m.docNumber as string) ?? "Credit note"}</span> · reason:{" "}
          {(m.reason as string) ?? "Not provided"}.
        </>
      ),
    };
  }
  if (entry.entityType === "invoice" && entry.verb === "sent") {
    return {
      tone: "sent",
      what: "Sent via WhatsApp",
      body: `Sent using ${(m.template as string) ?? "invoice template"}.`,
    };
  }
  if (entry.entityType === "invoice" && entry.verb === "issued") {
    return {
      tone: "issued",
      what: "Invoice issued",
      body: (
        <>
          Doc number <span className="num">{(m.docNumber as string) ?? "assigned"}</span> assigned.
          Total <span className="num">{formatPaise(m.totalPaise)}</span>.
        </>
      ),
    };
  }
  if (entry.entityType === "invoice" && entry.verb === "draft-updated") {
    return {
      tone: "draft",
      what: "Draft updated",
      body: (m.note as string) ?? "Invoice draft changed.",
    };
  }
  if (entry.entityType === "invoice" && entry.verb === "draft-created") {
    return {
      tone: "draft",
      what: "Draft created",
      body: "Invoice draft opened from the sales workspace.",
    };
  }
  if (entry.entityType === "approval") {
    return {
      tone: "reminder",
      what: approvalTitle(entry.verb),
      body: `${(m.kind as string) ?? "approval"} · ${(m.summary as string) ?? "No summary"}.`,
    };
  }
  return {
    tone: "draft",
    what: `${entry.verb} ${entry.entityType}`,
    body: null,
  };
}

function markerIcon(tone: ReturnType<typeof entryModel>["tone"]) {
  const props = { size: 10, strokeWidth: 2.4, "aria-hidden": true };
  if (tone === "paid") return <HandCoins {...props} />;
  if (tone === "sent") return <Send {...props} />;
  if (tone === "issued") return <Check {...props} />;
  if (tone === "reminder") return <Bell {...props} />;
  if (tone === "warn") return <FileMinus {...props} />;
  if (tone === "draft") return <Pencil {...props} />;
  return <FileText {...props} />;
}

function formatPaise(value: unknown): string {
  return typeof value === "string" ? paiseToInr(value) : "—";
}

function actorLabel(actorType: string): string {
  if (actorType === "web") return "Operator";
  if (actorType === "whatsapp") return "Operator (WhatsApp)";
  if (actorType === "api") return "API";
  if (actorType === "system") return "System";
  if (actorType === "ai") return "AI assistant";
  return actorType;
}

function approvalTitle(verb: string): string {
  if (verb === "requested") return "Approval requested";
  if (verb === "approved") return "Approval recorded";
  if (verb === "rejected") return "Approval rejected";
  return "Approval updated";
}
